import * as React from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Grid,
    Typography,
    Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { FilterCriteria, initFilterCriteriaObj } from "../../data/type";
import { LOCAL_CONSTANT } from "../Const";

// フィルタモーダルのプロパティ型定義
export interface FilterModalProps {
    open: boolean;
    onClose: () => void;
    onApplyFilter: (filters: FilterCriteria) => void;
    filterOptions: {
        editors: string[];
        branches: string[];
        pages: any[];
    };
    currentFilters: FilterCriteria;
}

// フィルタモーダルコンポーネント
const FilterModal: React.FC<FilterModalProps> = ({
    open,
    onClose,
    onApplyFilter,
    filterOptions,
    currentFilters,
}) => {
    // ボタン共通スタイル
    const commonButtonStyle = {
        margin: "0px 10px",
        display: "flex",
        alignItems: "center",
    };

    // キャンセルボタン専用スタイル
    const cancelButtonStyle = {
        ...commonButtonStyle,
        color: "#d32f2f",
        borderColor: "#d32f2f",
        backgroundColor: "white",
        "&:hover": {
            borderColor: "#d32f2f",
            backgroundColor: "white",
        }
    };

    // OKボタン専用スタイル
    const okButtonStyle = {
        ...commonButtonStyle,
    };

    // フィルタ条件の状態管理
    const [filters, setFilters] = React.useState<FilterCriteria>(currentFilters);

    // モーダルが開かれた時に現在のフィルタ条件をセット
    React.useEffect(() => {
        setFilters(currentFilters);
    }, [currentFilters, open]);


    // OKボタンクリック時の処理
    const handleApply = () => {
        onApplyFilter(filters);
        onClose();
    };

    // キャンセルボタンクリック時の処理
    const handleCancel = () => {
        setFilters(currentFilters);
        onClose();
    };

    // 日付入力フィールド変更ハンドラー
    const handleDateInputChange = (field: "startDate" | "endDate") => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        const date = value ? dayjs(value) : null;
        setFilters(prev => ({
            ...prev,
            [field]: date,
        }));
    };

    // クリアボタンクリック時の処理
    const handleClear = () => {
        setFilters(initFilterCriteriaObj);
        onApplyFilter(initFilterCriteriaObj); // クリアされたフィルターを適用
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: "500px",
                    height: "400px",
                }
            }}
        >
            <DialogContent sx={{ padding: "20px" }}>
                <Grid container spacing={3}>
                    {/* クリアボタン */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleClear}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.CLEAR}
                            </Button>
                        </Box>
                    </Grid>

                    {/* 日付フィールド */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <Typography variant="body1" sx={{ minWidth: "60px" }}>
                                {LOCAL_CONSTANT.LABEL.DATE}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={filters.startDate ? filters.startDate.format("YYYY-MM-DD") : ""}
                                    onChange={handleDateInputChange("startDate")}
                                    sx={{ width: "150px" }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                                <Typography variant="body1">〜</Typography>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={filters.endDate ? filters.endDate.format("YYYY-MM-DD") : ""}
                                    onChange={handleDateInputChange("endDate")}
                                    sx={{ width: "150px" }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* 修正者フィールド */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <Typography variant="body1" sx={{ minWidth: "60px" }}>
                                {LOCAL_CONSTANT.LABEL.EDITOR}
                            </Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                id="AutocompEditor"
                                limitTags={1}
                                options={filterOptions.editors}
                                value={filters.editor}
                                getOptionLabel={(option) => option}
                                isOptionEqualToValue={(option, value) => option === value}
                                onChange={(e, val, sit) => {
                                    if (sit === "removeOption") {
                                        document.getElementById("AutocompEditor")?.focus();
                                    }
                                    setFilters(prev => ({
                                        ...prev,
                                        editor: val
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                    />
                                )}
                                sx={{ width: "335px" }}
                            />
                        </Box>
                    </Grid>

                    {/* 枝フィールド */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <Typography variant="body1" sx={{ minWidth: "60px" }}>
                                {LOCAL_CONSTANT.LABEL.BRANCH}
                            </Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                id="AutocompBranch"
                                limitTags={1}
                                options={filterOptions.branches}
                                value={filters.branch}
                                getOptionLabel={(option) => option}
                                isOptionEqualToValue={(option, value) => option === value}
                                onChange={(e, val, sit) => {
                                    if (sit === "removeOption") {
                                        document.getElementById("AutocompBranch")?.focus();
                                    }
                                    setFilters(prev => ({
                                        ...prev,
                                        branch: val
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                    />
                                )}
                                sx={{ width: "335px" }}
                            />
                        </Box>
                    </Grid>

                    {/* ページフィールド */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <Typography variant="body1" sx={{ minWidth: "60px" }}>
                                {LOCAL_CONSTANT.LABEL.PAGE}
                            </Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                id="AutocompPage"
                                limitTags={1}
                                options={filterOptions.pages}
                                value={filterOptions.pages.filter(page => filters.page.includes(page.code_value))}
                                getOptionLabel={(option) => option.code_display}
                                isOptionEqualToValue={(option, value) => option.code_value === value.code_value}
                                onChange={(e, val, sit) => {
                                    if (sit === "removeOption") {
                                        document.getElementById("AutocompPage")?.focus();
                                    }
                                    setFilters(prev => ({
                                        ...prev,
                                        page: val.map(item => item.code_value)
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                    />
                                )}
                                sx={{ width: "335px" }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* ボタン */}
            <DialogActions sx={{ justifyContent: "center", padding: "20px", gap: "20px" }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancel}
                    sx={cancelButtonStyle}
                >
                    {LOCAL_CONSTANT.LABEL.CANCEL}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleApply}
                    sx={okButtonStyle}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilterModal;
